// src/pages/Login/LoginPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import "./LoginPage.css";

import { loginUser, registerUser, verifyEmailToken } from "../api/index.jsx";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const [isLoading, setIsLoading] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const headerRef = useRef(null);

  const redirect = searchParams.get("redirect") || "/";

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      verifyEmail(token);
    }
  }, [searchParams]);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (token && isLoggedIn === "true") {
      navigate(redirect, { replace: true });
    }
  }, [navigate, redirect]);

  const verifyEmail = async (token) => {
    setIsLoading(true);
    try {
      const result = await verifyEmailToken(token);
      if (result.success) {
        setVerificationMessage(
          result.message || "Email verified successfully! You can now login."
        );
        setSuccessMessage("Email verified successfully! You can now login.");

        if (result.token) {
          localStorage.setItem("token", result.token);
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userEmail", result.email || formData.email);

          if (result.data) {
            localStorage.setItem("userData", JSON.stringify(result.data));
            if (result.data.name) {
              localStorage.setItem("userName", result.data.name);
            }
          }

          setTimeout(() => {
            navigate(redirect, { replace: true });
          }, 2000);
        }
      } else {
        setVerificationMessage(result.message || "Email verification failed.");
        setError(result.message || "Email verification failed.");
      }
    } catch (error) {
      console.error("Email verification error:", error);
      setVerificationMessage("Email verification failed. Please try again.");
      setError("Email verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (error) setError("");
    if (successMessage) setSuccessMessage("");
    if (verificationMessage) setVerificationMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoginMode && !formData.name) {
      setError("Please enter your name");
      return;
    }

    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    setVerificationMessage("");

    try {
      if (isLoginMode) {
        const result = await loginUser({
          email: formData.email,
          password: formData.password
        });

        if (result.success && result.token) {
          localStorage.setItem("token", result.token);
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userEmail", formData.email);

          if (result.data) {
            localStorage.setItem("userData", JSON.stringify(result.data));
            if (result.data.name) {
              localStorage.setItem("userName", result.data.name);
            }
          }

          navigate(redirect, { replace: true });
        } else {
          setError(result.message || "Invalid email or password");
        }
      } else {
        const result = await registerUser({
          name: formData.name,
          email: formData.email,
          password: formData.password
        });

        console.log("Registration response:", result);

        if (Array.isArray(result)) {
          console.error("Unexpected array response from server:", result);
          setError(
            "Server returned unexpected response format. Please try again."
          );
          return;
        }

        if (result && result.success) {
          if (result.token) {
            localStorage.setItem("token", result.token);
            localStorage.setItem("isLoggedIn", "true");
            localStorage.setItem("userEmail", formData.email);

            if (result.data) {
              localStorage.setItem("userData", JSON.stringify(result.data));
              if (result.data.name) {
                localStorage.setItem("userName", result.data.name);
              }
            }

            navigate(redirect, { replace: true });
          } else {
            setSuccessMessage(
              result.message ||
                "Registration successful! Please check your email to verify your account."
            );

            setFormData({
              name: "",
              email: "",
              password: ""
            });

            setTimeout(() => {
              setIsLoginMode(true);
              setSuccessMessage("");
            }, 3000);
          }
        } else {
          const errorMsg =
            result?.message ||
            result?.error ||
            result?.data?.message ||
            "Registration failed. Please try again.";
          setError(errorMsg);
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData)) {
          setError("Server error. Please try again later.");
        } else if (errorData.message) {
          setError(errorData.message);
        } else if (errorData.error) {
          setError(errorData.error);
        } else {
          setError("An error occurred. Please try again.");
        }
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setIsLoginMode(false);
    setError("");
    setSuccessMessage("");
    setVerificationMessage("");
    setFormData({
      name: "",
      email: "",
      password: ""
    });
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setError("");
    setSuccessMessage("");
    setVerificationMessage("");
    setFormData({
      name: "",
      email: "",
      password: ""
    });
  };

  return (
    <>
      <div ref={headerRef}>
        <Header forceSolid />
      </div>

      {/* HEIGHT FIX: 80px bottom se kam kiya taaki footer jaisi gap na rahe */}
      <div
        className="auth-page"
        style={{ height: `calc(100vh - ${headerHeight}px - 80px)` }}
      >
        <div className="auth-container">
          <h1 className="auth-heading">
            {isLoginMode ? "Login" : "Create Account"}
          </h1>

          {verificationMessage && (
            <div
              className={`verification-message ${
                verificationMessage.includes("successfully") ? "success" : "error"
              }`}
            >
              <strong>
                {verificationMessage.includes("successfully") ? "Success:" : "Error:"}
              </strong>{" "}
              {verificationMessage}
            </div>
          )}

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          {successMessage && (
            <div className="success-message">
              <strong>Success:</strong> {successMessage}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            {!isLoginMode && (
              <div className="input-group">
                <input
                  className="auth-input"
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required={!isLoginMode}
                  disabled={isLoading}
                  autoComplete="name"
                />
              </div>
            )}

            <div className="input-group">
              <input
                className="auth-input"
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="input-group">
              <input
                className="auth-input"
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete={isLoginMode ? "current-password" : "new-password"}
              />
            </div>

            {isLoginMode && (
              <div className="forgot-password-container">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="forgot-text"
                  disabled={isLoading}
                >
                  Forgot your password?
                </button>
              </div>
            )}

            <button className="auth-btn" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  {isLoginMode ? "SIGN IN" : "CREATING..."}
                </>
              ) : isLoginMode ? (
                "SIGN IN"
              ) : (
                "CREATE ACCOUNT"
              )}
            </button>

            <div className="auth-toggle-container">
              <button
                type="button"
                className="auth-toggle-link"
                onClick={toggleMode}
                disabled={isLoading}
              >
                {isLoginMode ? "Create account" : "Login"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginPage;